import { useState, useCallback, useMemo } from "react";
import { View, Text, StyleSheet, Pressable, TextInput } from "react-native";
import { useNavigation } from "@react-navigation/native";
import { faX, faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import AppStore from "../../storage/appStore";
import { FontAwesomeIcon, homeIconSizes, tabIconSize, platform, logAnalyticsEvent, logError } from "../../platform";
import { subscribeEmailOrSms } from "../../utils/api/subscriptionApi";
import { formatPhoneNumber, isPhoneValid } from "../../utils/stringUtils";
import { COLORS } from "../../theme/colors";
import { STRINGS } from "../../constants/strings";
import SEOHeaderTags from "../SEOHeaderTags";
import LoadingIndicator from "../../sub/components/LoadingIndicator";
import HoneypotInput from "../../sub/components/HoneypotInput";
import { useFunctionDisable } from "../../utils/functionDisable";
import validTlds from "../../utils/validTlds";
import validator from "validator";

const NotificationSubscription = () => {
  const navigation = useNavigation();
  const propertyInfo = AppStore((state) => state.propertyInfo);
  const setNotificationSubscription = AppStore((state) => state.setNotificationSubscription);
  const notificationSubscription = AppStore((state) => state.notificationSubscription);
  const setErrorMessage = AppStore((state) => state.setErrorMessage);
  const submitEnabled = useFunctionDisable(3000);
  const [emailOrPhone, setEmailOrPhone] = useState("");
  const [emailOrPhoneValid, setEmailOrPhoneValid] = useState(false);
  const [apiResponse, setApiResponse] = useState("");
  const [loading, setLoading] = useState(false);

  /**
   * Handles changes to the email input field.
   * Filters out invalid characters and updates the user info state.
   * Also validates the filtered email and updates the validity state.
   *
   * @param {string} text - The input text from the email field.
   */
  const handleEmailChange = useCallback((text) => {
    const filtered = text.replace(/[^a-zA-Z0-9@._+-]/g, "");
    setEmailOrPhone(filtered);

    const tld = filtered.split(".").pop().toLowerCase();
    setEmailOrPhoneValid(validator.isEmail(filtered) && validTlds.has(tld));
  }, []);

  /**
   * Handles changes to the phone number input field.
   * Formats the input as a US phone number (XXX-XXX-XXXX) as the user types,
   * allowing only numeric characters and limiting input to 10 digits.
   * Updates the user info state with the formatted value and sets validity
   * based on whether the input contains exactly 10 digits.
   *
   * @param {string} text - The raw input string from the phone number field.
   */
  const handlePhoneChange = useCallback((text) => {
    const formatted = formatPhoneNumber(text);
    setEmailOrPhone(formatted);
    setEmailOrPhoneValid(isPhoneValid(text));
  }, []);

  /**
   * Handles the subscription process for email or SMS notifications.
   *
   * Cleans up any previous state, processes the user information based on the subscription type,
   * and attempts to subscribe the user using the provided information. Updates the UI with the
   * response message on success, or displays an error message on failure.
   *
   * @async
   * @param {"email"|"text"} type - The type of subscription to process ("email" or "text").
   * @returns {Promise<void>} Resolves when the subscription process is complete.
   */
  const subscribePressHandler = async (type) => {
    if (!submitEnabled) return;
    try {
      setLoading(true);
      let processedEmailOrPhone;
      if (type === "email") {
        processedEmailOrPhone = emailOrPhone;
      } else if (type === "text") {
        processedEmailOrPhone = emailOrPhone.replace(/-/g, "");
      } else {
        throw new Error("Invalid subscription type");
      }
      const response = await subscribeEmailOrSms(type, processedEmailOrPhone, propertyInfo?.garbage?.garbageDay);
      setApiResponse(response.message);
      cleanUp();
    } catch (error) {
      logError("subscription_failed", {
        message: error.message,
        stack: error.stack,
        type,
        garbageDay: propertyInfo?.garbage?.garbageDay,
      });
      fullCleanUp();
      setErrorMessage(STRINGS.failedToSubscribe);
    } finally {
      setLoading(false);
    }
  };

  const cleanUp = useCallback(() => {
    setEmailOrPhone("");
    setEmailOrPhoneValid(false);
  }, []);

  const fullCleanUp = useCallback(() => {
    cleanUp();
    setApiResponse("");
    setNotificationSubscription({ show: false, type: "" });
  }, [cleanUp, setNotificationSubscription]);

  // Memoize accessibility state objects
  const backgroundAccessibility = useMemo(
    () => ({
      accessible: true,
      accessibilityRole: "button",
      accessibilityLabel: "Close notifications by tapping background",
    }),
    []
  );

  const closeButtonAccessibility = useMemo(
    () => ({
      accessible: true,
      accessibilityRole: "button",
      accessibilityLabel: "Close notifications",
    }),
    []
  );

  const clearButtonAccessibility = useMemo(
    () => ({
      accessible: true,
      accessibilityRole: "button",
      accessibilityLabel: "Clear input",
    }),
    []
  );

  // Memoize text accessibility props
  const textAccessibility = useMemo(
    () => ({
      accessible: true,
      accessibilityRole: "text",
      maxFontSizeMultiplier: 1.2,
    }),
    []
  );

  // Memoize input placeholder and keyboard type
  const inputPlaceholder = useMemo(() => {
    return notificationSubscription.type === "email"
      ? STRINGS.emailAddressPlaceholder
      : notificationSubscription.type === "text"
      ? STRINGS.phoneNumberPlaceholder
      : "";
  }, [notificationSubscription.type]);

  const inputKeyboardType = useMemo(() => {
    return notificationSubscription.type === "email" ? "email-address" : "number-pad";
  }, [notificationSubscription.type]);

  // Memoize subscribe button style
  const subscribeButtonStyle = useMemo(
    () => [styles.buttonContainer, !emailOrPhoneValid && styles.buttonContainerDisabled],
    [emailOrPhoneValid]
  );

  // Memoize subscribe button accessibility
  const subscribeButtonAccessibility = useMemo(
    () => ({
      accessible: true,
      accessibilityRole: "button",
      accessibilityLabel: `Sign up for ${notificationSubscription.type} subscription`,
    }),
    [notificationSubscription.type]
  );

  // Memoize navigation handler
  const handleNavigateToTerms = useCallback(() => {
    setEmailOrPhone("");
    setApiResponse("");
    setNotificationSubscription({ show: false, type: "" });
    navigation.navigate("Terms");
  }, [navigation, setNotificationSubscription]);

  return (
    <View style={styles.overlayContainer}>
      <Pressable
        style={styles.backgroundPressable}
        onPress={fullCleanUp}
        {...backgroundAccessibility}
        testID="background-pressable"
      />
      <LoadingIndicator isLoading={loading} />
      <View style={styles.notificationBox}>
        <Pressable style={styles.closeButton} onPress={fullCleanUp} {...closeButtonAccessibility}>
          <FontAwesomeIcon icon={faX} color={COLORS.tertiary} size={tabIconSize} />
        </Pressable>
        <View style={styles.container}>
          <SEOHeaderTags style={styles.headerText} text="Garbage and Recycling Notifications" tag="h2" />
          <Text style={styles.infoText} {...textAccessibility}>
            {STRINGS.subscriptionHeader}
          </Text>

          <View style={styles.subscriptionContainer}>
            {apiResponse === "" ? (
              <>
                <Text style={styles.subscriptionText} {...textAccessibility}>
                  {notificationSubscription.type === "email"
                    ? STRINGS.emailAddress
                    : notificationSubscription.type === "text"
                    ? STRINGS.phoneNumber
                    : ""}
                </Text>

                <View style={styles.inputWrapper}>
                  <TextInput
                    style={styles.inputText}
                    value={emailOrPhone}
                    onChangeText={
                      notificationSubscription.type === "email"
                        ? handleEmailChange
                        : notificationSubscription.type === "text"
                        ? handlePhoneChange
                        : undefined
                    }
                    maxFontSizeMultiplier={1.2}
                    placeholder={inputPlaceholder}
                    keyboardType={inputKeyboardType}
                    placeholderTextColor={COLORS.tertiary}
                    accessibilityLabel={inputPlaceholder}
                    accessibilityHint={`Enter your ${
                      notificationSubscription.type === "email" ? "email address" : "phone number"
                    } for notifications`}
                  />

                  <HoneypotInput
                    name={notificationSubscription.type === "email" ? "email_subscribe" : "sms_subscribe"}
                    analytics={logAnalyticsEvent}
                  />

                  {emailOrPhone.length > 0 && (
                    <Pressable
                      testID="clear-user-info-button"
                      style={styles.clearButton}
                      onPress={cleanUp}
                      {...clearButtonAccessibility}
                    >
                      <FontAwesomeIcon icon={faCircleXmark} size={homeIconSizes.button} color={COLORS.text} />
                    </Pressable>
                  )}
                </View>

                <Pressable
                  testID="signup-button"
                  style={subscribeButtonStyle}
                  disabled={!emailOrPhoneValid}
                  onPress={() => subscribePressHandler(notificationSubscription.type)}
                  {...subscribeButtonAccessibility}
                >
                  <Text style={styles.buttonText} maxFontSizeMultiplier={1.2}>
                    {STRINGS.signup}
                  </Text>
                </Pressable>
              </>
            ) : (
              <Text style={styles.responseText} {...textAccessibility}>
                {apiResponse}
              </Text>
            )}
          </View>

          {apiResponse === "" && (
            <Text style={styles.infoText} {...textAccessibility}>
              {notificationSubscription.type === "email" ? (
                STRINGS.emailSubscriptionTermsOfService
              ) : (
                <>
                  {STRINGS.smsSubscriptionTermsOfService}
                  <Text
                    style={styles.linkText}
                    accessible={true}
                    accessibilityRole="link"
                    maxFontSizeMultiplier={1.2}
                    onPress={handleNavigateToTerms}
                  >
                    here
                  </Text>
                  .
                </>
              )}
            </Text>
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  overlayContainer: {
    position: "absolute",
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1,
  },
  backgroundPressable: {
    position: "absolute",
    width: "100%",
    height: "100%",
    backgroundColor: COLORS.secondaryTranslucent,
  },
  notificationBox: {
    backgroundColor: COLORS.background,
    borderRadius: 8,
    paddingVertical: 20,
    paddingHorizontal: 20,
    width: "95%",
    maxWidth: 500,
    zIndex: 2,
  },
  closeButton: {
    alignSelf: "flex-end",
    marginRight: -10,
  },
  container: {
    justifyContent: "center",
    alignItems: "center",
  },
  headerText: {
    textAlign: "center",
    marginTop: 5,
    ...(platform !== "web" && {
      fontSize: 26,
      paddingBottom: 10,
      fontWeight: "bold",
    }),
  },
  subscriptionContainer: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.tertiaryVariant,
    backgroundColor: COLORS.tertiaryVariant,
    width: "100%",
    padding: 20,
    marginBottom: 20,
  },
  infoText: {
    fontSize: 14,
    textAlign: "center",
    marginBottom: 20,
    lineHeight: 20,
  },
  subscriptionText: {
    fontSize: 16,
    marginBottom: 10,
    fontWeight: "bold",
  },
  inputWrapper: {
    position: "relative",
    width: "100%",
  },
  inputText: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.tertiary,
    fontSize: 14,
    padding: 10,
    paddingRight: 40,
    marginBottom: 20,
    color: COLORS.uiGray900,
    backgroundColor: COLORS.background,
  },
  clearButton: {
    position: "absolute",
    right: 10,
    top: 12,
  },
  buttonContainer: {
    borderRadius: 25,
    width: 130,
    backgroundColor: COLORS.red,
    alignSelf: "center",
  },
  buttonContainerDisabled: {
    backgroundColor: COLORS.tertiary,
  },
  buttonText: {
    fontSize: 14,
    color: COLORS.onPrimary,
    padding: 12,
    textAlign: "center",
    fontWeight: "bold",
  },
  responseText: {
    fontSize: 18,
    textAlign: "center",
    fontWeight: "bold",
  },
  linkText: {
    color: COLORS.primary,
    textDecorationLine: "underline",
  },
});

NotificationSubscription.displayName = "NotificationSubscription";

export default NotificationSubscription;
